import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { PostCard, type FeedPost } from "@/components/post-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CATEGORIES } from "@/lib/categories";
import { formatDistanceToNow } from "date-fns";
import { MapPin, MessageCircle, AlertTriangle, Loader2, CheckCircle2, ThumbsUp, ShieldCheck, Pin } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AccountBadge, isOfficial, ACCOUNT_META, type AccountType } from "@/components/account-badge";
import { ReportIntelligence } from "@/components/report-intelligence";
import { RelatedReports } from "@/components/related-reports";
import { STATUS_META } from "@/lib/reports";

export const Route = createFileRoute("/posts/$id")({
  loader: async ({ params }) => {
    try {
      const { data } = await supabase
        .from("posts")
        .select("title, content, created_at, location_name")
        .eq("id", params.id)
        .maybeSingle();
      return { seo: data ?? null };
    } catch {
      return { seo: null };
    }
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.seo as
      | { title: string | null; content: string | null; created_at: string | null; location_name: string | null }
      | null
      | undefined;
    const heading = p?.title?.trim() || "Community report";
    const title = `${heading} — Xamseen`;
    const description =
      (p?.content || "").replace(/\s+/g, " ").trim().slice(0, 155) ||
      "Read this community report on Xamseen — what people are seeing, verifying and resolving locally.";
    const url = `https://xamseen.com/posts/${params.id}`;
    return {
      meta: [
        { title: title.slice(0, 60) },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: heading,
            description,
            url,
            ...(p?.created_at ? { datePublished: p.created_at } : {}),
            ...(p?.location_name ? { contentLocation: { "@type": "Place", name: p.location_name } } : {}),
          }),
        },
      ],
    };
  },
  component: PostPage,
});


interface Comment {
  id: string;
  content: string;
  created_at: string;
  upvotes: number;
  is_solution: boolean;
  parent_id: string | null;
  author: {
    id: string; username: string; full_name: string | null; avatar_url: string | null;
    account_type?: AccountType | null; verified?: boolean | null;
  } | null;
}

function PostPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [post, setPost] = useState<FeedPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [accepting, setAccepting] = useState<string | null>(null);

  const isPostAuthor = !!user && !!post?.author && post.author.id === user.id;

  const acceptSolution = async (commentId: string) => {
    setAccepting(commentId);
    const { error } = await supabase.rpc("accept_solution", { _post_id: id, _comment_id: commentId });
    setAccepting(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Solution accepted — report marked resolved");
    await load();
  };

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("posts")
      .select(`
        id, title, content, category, post_type, status, image_url, location_name,
        upvotes, comment_count, created_at, severity, cluster_id,
        author:profiles!posts_author_profile_fkey ( id, username, full_name, avatar_url, account_type, verified ),
        community:communities ( id, name, slug )
      `)
      .eq("id", id)
      .maybeSingle();

    if (data) {
      const [vr, fr, sr, mv, mf, ms] = await Promise.all([
        supabase.from("reactions").select("id", { count: "exact", head: true }).eq("post_id", id).eq("reaction", "verify" as any),
        supabase.from("post_followers").select("user_id", { count: "exact", head: true }).eq("post_id", id),
        supabase.from("comments").select("id", { count: "exact", head: true }).eq("post_id", id).eq("is_solution", true),
        user ? supabase.from("reactions").select("id").eq("user_id", user.id).eq("post_id", id).eq("reaction", "verify" as any).maybeSingle() : Promise.resolve({ data: null }),
        user ? supabase.from("post_followers").select("user_id").eq("user_id", user.id).eq("post_id", id).maybeSingle() : Promise.resolve({ data: null }),
        user ? supabase.from("votes").select("user_id").eq("user_id", user.id).eq("post_id", id).eq("vote_type", "up" as any).maybeSingle() : Promise.resolve({ data: null }),
      ]);
      (data as any).support_count = (data as any).upvotes ?? 0;
      (data as any).verify_count = vr.count ?? 0;
      (data as any).follower_count = fr.count ?? 0;
      (data as any).solution_count = sr.count ?? 0;
      (data as any).my_verify = !!mv.data;
      (data as any).my_follow = !!mf.data;
      (data as any).my_support = !!ms.data;
    }
    setPost(data as any);

    const { data: cs } = await supabase
      .from("comments")
      .select(`id, content, created_at, upvotes, is_solution, parent_id,
        author:profiles!comments_author_profile_fkey ( id, username, full_name, avatar_url, account_type, verified )`)
      .eq("post_id", id)
      .order("created_at", { ascending: true });
    setComments((cs ?? []) as any);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.id]);

  const submitComment = async () => {
    if (!user || !newComment.trim()) return;
    setPosting(true);
    const { error } = await supabase.from("comments").insert({
      post_id: id, author_id: user.id, content: newComment.trim(),
    });
    setPosting(false);
    if (error) return toast.error(error.message);
    setNewComment("");
    load();
  };

  if (loading) return <AppShell allowAnonymous><div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;
  if (!post) return <AppShell allowAnonymous><div className="py-20 text-center text-muted-foreground">Post not found.</div></AppShell>;

  const cat = CATEGORIES[post.category];
  const isEmergency = post.post_type === "emergency" || post.category === "emergency";

  return (
    <AppShell allowAnonymous>
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0">
          <article className={cn(
            "rounded-2xl border bg-card p-6 shadow-[var(--shadow-card)]",
            isEmergency ? "border-emergency/40 bg-emergency/5" : "border-border"
          )}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {post.community && (
                <>
                  <Link to="/communities/$slug" params={{ slug: post.community.slug }} className="font-medium text-foreground hover:underline">{post.community.name}</Link>
                  <span>·</span>
                </>
              )}
              {post.author && (
                <Link to="/profile/$username" params={{ username: post.author.username }} className="flex items-center gap-1.5 hover:text-foreground">
                  <Avatar className="h-5 w-5"><AvatarImage src={post.author.avatar_url ?? undefined} /><AvatarFallback className="text-[10px]">{(post.author.username[0] ?? "U").toUpperCase()}</AvatarFallback></Avatar>
                  <span className={cn(isOfficial((post.author as any).account_type) && "text-foreground font-medium")}>{post.author.username}</span>
                  <AccountBadge accountType={(post.author as any).account_type} verified={(post.author as any).verified} />
                </Link>
              )}
              <span>·</span>
              <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
              {post.location_name && <><span>·</span><span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{post.location_name}</span></>}
            </div>

            <h1 className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
              {isEmergency && <AlertTriangle className="mr-2 inline h-5 w-5 -translate-y-1 text-emergency" />}
              {post.title}
            </h1>

            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="secondary" className="gap-1 font-normal"><cat.icon className="h-3 w-3" style={{ color: cat.color }} />{cat.label}</Badge>
              {post.status === "resolved" ? (
                <Badge className="gap-1 bg-success/15 text-success"><CheckCircle2 className="h-3 w-3" />Resolved</Badge>
              ) : (
                <Badge variant="outline" className="font-normal">{STATUS_META[post.status]?.label ?? post.status}</Badge>
              )}
            </div>

            <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{post.content}</p>

            {post.image_url && (
              <div className="mt-5 overflow-hidden rounded-xl border border-border">
                <img src={post.image_url} alt="" className="w-full" />
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-border pt-4 text-sm">
              <EngagementBar post={post} onChange={load} />
              <div className="ml-auto flex items-center gap-1.5 text-muted-foreground">
                <MessageCircle className="h-4 w-4" />{post.comment_count} comments
              </div>
            </div>
          </article>

          <ReportIntelligence postId={post.id} status={post.status} onStatusChange={load} />

          <div className="mt-4">
            <RelatedReports clusterId={(post as any).cluster_id} excludePostId={post.id} />
          </div>

          <section className="mt-6">
            <h2 className="font-display text-lg font-semibold">Comments</h2>
            {user ? (
              <div className="mt-3 rounded-xl border border-border bg-card p-3 shadow-[var(--shadow-card)]">
                <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Share your thoughts, propose a solution, ask a follow-up…" rows={3} maxLength={2000} />
                <div className="mt-2 flex justify-end">
                  <Button onClick={submitComment} disabled={posting || !newComment.trim()} size="sm">
                    {posting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Post comment
                  </Button>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground"><Link to="/auth" className="text-primary underline">Sign in</Link> to join the discussion.</p>
            )}

            <div className="mt-5 grid gap-3">
              {comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No comments yet — be the first.</p>
              ) : (
                comments.map((c) => {
                  const off = isOfficial(c.author?.account_type);
                  const meta = off ? ACCOUNT_META[c.author!.account_type as Exclude<AccountType, "citizen">] : null;
                  return (
                  <div key={c.id} className={cn(
                    "rounded-xl border border-border bg-card p-4",
                    c.is_solution && "border-success/40 bg-success/5",
                    off && !c.is_solution && "border-primary/40 bg-primary/5",
                  )}>
                    {off && meta && (
                      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                        <meta.icon className="h-3 w-3" /> Official response · {meta.short}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {c.author && (
                        <Link to="/profile/$username" params={{ username: c.author.username }} className="flex items-center gap-1.5 hover:text-foreground">
                          <Avatar className="h-5 w-5"><AvatarImage src={c.author.avatar_url ?? undefined} /><AvatarFallback className="text-[10px]">{(c.author.username[0] ?? "U").toUpperCase()}</AvatarFallback></Avatar>
                          <span className="font-medium text-foreground">{c.author.username}</span>
                          <AccountBadge accountType={c.author.account_type} verified={c.author.verified} />
                        </Link>
                      )}
                      <span>·</span>
                      <span>{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
                      {c.is_solution && <Badge className="ml-1 gap-1 bg-success/15 text-success"><CheckCircle2 className="h-3 w-3" />Accepted solution</Badge>}
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{c.content}</p>
                    {isPostAuthor && !c.is_solution && c.author?.id !== user?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 h-8 gap-1.5 text-xs"
                        disabled={accepting === c.id}
                        onClick={() => acceptSolution(c.id)}
                      >
                        {accepting === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        Accept as solution
                      </Button>
                    )}
                  </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-20 rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-card)]">
            <p className="font-display text-sm font-medium">About this post</p>
            <dl className="mt-3 grid gap-2 text-xs">
              <Row label="Type" value={post.post_type} />
              <Row label="Category" value={cat.label} />
              <Row label="Status" value={post.status} />
              <Row label="Support" value={`${post.support_count ?? 0}`} />
              <Row label="Confirmations" value={`${post.verify_count ?? 0}`} />
              <Row label="Solutions" value={`${post.solution_count ?? 0}`} />
              <Row label="Followers" value={`${post.follower_count ?? 0}`} />
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">
              Engagement tracks real community impact — not popularity.
            </p>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium capitalize">{value}</dd>
    </div>
  );
}

function EngagementBar({ post, onChange }: { post: FeedPost; onChange: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState({ on: !!post.my_support, n: post.support_count ?? 0 });
  const [v, setV] = useState({ on: !!post.my_verify, n: post.verify_count ?? 0 });
  const [f, setF] = useState({ on: !!post.my_follow, n: post.follower_count ?? 0 });

  const need = () => { if (!user) { toast.error("Sign in to engage"); return false; } return true; };

  const doSupport = async () => {
    if (!need() || busy) return; setBusy(true);
    const next = !s.on; setS({ on: next, n: s.n + (next ? 1 : -1) });
    if (next) await supabase.from("votes").upsert({ user_id: user!.id, post_id: post.id, vote_type: "up" as any }, { onConflict: "user_id,post_id" });
    else await supabase.from("votes").delete().eq("user_id", user!.id).eq("post_id", post.id);
    setBusy(false); onChange();
  };
  const doVerify = async () => {
    if (!need() || busy) return; setBusy(true);
    const next = !v.on; setV({ on: next, n: v.n + (next ? 1 : -1) });
    if (next) await supabase.from("reactions").insert({ user_id: user!.id, post_id: post.id, reaction: "verify" as any });
    else await supabase.from("reactions").delete().eq("user_id", user!.id).eq("post_id", post.id).eq("reaction", "verify" as any);
    setBusy(false); onChange();
  };
  const doFollow = async () => {
    if (!need() || busy) return; setBusy(true);
    const next = !f.on; setF({ on: next, n: f.n + (next ? 1 : -1) });
    if (next) await supabase.from("post_followers").insert({ user_id: user!.id, post_id: post.id });
    else await supabase.from("post_followers").delete().eq("user_id", user!.id).eq("post_id", post.id);
    setBusy(false); onChange();
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button variant="ghost" size="sm" disabled={busy} onClick={doSupport} className={cn("h-8 gap-1.5 px-2.5", s.on && "bg-primary/10 text-primary")}>
        <ThumbsUp className="h-4 w-4" /> Support <span className="tabular-nums">{s.n}</span>
      </Button>
      <Button variant="ghost" size="sm" disabled={busy} onClick={doVerify} className={cn("h-8 gap-1.5 px-2.5", v.on && "bg-success/15 text-success")}>
        <ShieldCheck className="h-4 w-4" /> I see this too <span className="tabular-nums">{v.n}</span>
      </Button>
      <Button variant="ghost" size="sm" disabled={busy} onClick={doFollow} className={cn("h-8 gap-1.5 px-2.5", f.on && "bg-accent text-accent-foreground")}>
        <Pin className="h-4 w-4" /> Follow <span className="tabular-nums">{f.n}</span>
      </Button>
    </div>
  );
}
